/**
 * Keycloak Authentication Provider
 * Manages Keycloak authentication state and provides auth context to the app
 */

import Keycloak from 'keycloak-js'
import { createContext, type ReactNode,useContext, useEffect, useRef, useState } from 'react'

interface KeycloakContextValue {
  keycloak: Keycloak | null
  initialized: boolean
  authenticated: boolean
  token: string | null
  login: () => void
  logout: () => void
}

const KeycloakContext = createContext<KeycloakContextValue | null>(null)

interface KeycloakProviderProps {
  children: ReactNode
}

export const KeycloakProvider = ({ children }: KeycloakProviderProps) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const initStarted = useRef(false)
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initStarted.current) {
      return
    }
    initStarted.current = true

    const initKeycloak = async () => {
      const keycloakInstance = new Keycloak({
        url: import.meta.env.VITE_OIDC_BASE_URL,
        realm: import.meta.env.VITE_REALM,
        clientId: import.meta.env.VITE_CLIENT_ID,
      })

      try {
        const authenticated = await keycloakInstance.init({
          onLoad: 'check-sso',
          checkLoginIframe: false,
          pkceMethod: 'S256',
        })

        if (!authenticated) {
          void keycloakInstance.login(
            import.meta.env.VITE_KC_IDP_HINT
              ? { idpHint: import.meta.env.VITE_KC_IDP_HINT }
              : undefined
          )
          return
        }
        setKeycloak(keycloakInstance)
        setAuthenticated(authenticated)
        setToken(keycloakInstance.token || null)
        setInitialized(true)

        if (authenticated) {
          refreshIntervalRef.current = setInterval(() => {
            keycloakInstance
              .updateToken(30)
              .then((refreshed) => {
                if (refreshed) {
                  setToken(keycloakInstance.token || null)
                }
              })
              .catch(() => {
                console.error('Failed to refresh token')
              })
          }, 30000)
        }
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error)
        setInitialized(true)
      }
    }

    void initKeycloak()

    // clearInterval is in the effect cleanup, not inside the async callback,
    // so it actually runs on unmount.
    return () => {
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [])

  const login = () => {
    void keycloak?.login({
      ...(import.meta.env.VITE_KC_IDP_HINT && {
        idpHint: import.meta.env.VITE_KC_IDP_HINT,
      }),
    })
  }

  const logout = () => {
    void keycloak?.logout()
  }

  const value: KeycloakContextValue = {
    keycloak,
    initialized,
    authenticated,
    token,
    login,
    logout,
  }

  // Show loading state while initializing
  if (!initialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif'
      }}>
        Initializing authentication...
      </div>
    )
  }

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  )
}

export const useKeycloak = () => {
  const context = useContext(KeycloakContext)
  if (!context) {
    throw new Error('useKeycloak must be used within KeycloakProvider')
  }
  return context
}
